import React from 'react';
import { FormGroup } from 'reactstrap';
import { faRotate, faUserClock } from '@fortawesome/free-solid-svg-icons';
import i18n from '@s20/i18n';
import { email } from '@s20/common/util/validation';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes } from '../Common';
import { Modal } from '../common/Modal';
import {
    FormLabel,
    FlexCenterRow,
    toasty,
    onReactSelectChanged,
    FormCheckbox,
} from '../common/forms/FormElements';
import SlideForm from '../common/forms/SlideForm';
import { BaseFormViewModel } from '../common/ViewModel';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { SaveButton } from '../common/forms/SaveButton';
import { SignatureReviewTable } from './SignatureReviewTable';
import { ClientTimesheetSignature } from './ClientTimesheetSignature';
import './ClientReview.scss';

export const TimesheetSignatureType = {
    ClientForeman: 1,
    ClientOtherAuthorized: 2,
    ClientNoSignature: 3,
    Employee: 4,
    EmployeeNoSignature: 5,
};

export default class ClientReview extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const stateBase = {
            review: {},
            formValidated: false,
            isOpen: false,
            signatureTypes: [],
            currentEmailAddress: '',
            specificEmailAdds: [],
            sigIsComplete: false,
            displayReuse: false,
            ...new BaseFormViewModel(),
        };
        this.baseState = stateBase;
        this.state = stateBase;
        this.resetForm = this.resetForm.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
    }

    onChangeNotes = (e) => {
        const { review } = { ...this.state };
        review.notes = e.target.value;
        this.setState({ review });
    };

    onClose = () => {
        this.resetForm();
        this.props.toggleShow(false);
        this.context.setFormOpened(false);
        this.props.onClose();
    };


    onEmailAdded = () => {
        const { currentEmailAddress, review } = { ...this.state };
        if (currentEmailAddress) {
            const emailAddress = currentEmailAddress.trim();
            const match = email(emailAddress);
            if (match) {
                if (!(review.emailAddresses ?? []).length) review.emailAddresses = [];
                if (!review.emailAddresses.includes(emailAddress)) review.emailAddresses.push(emailAddress);
                this.setState({ review: { ...review }, currentEmailAddress: null });
            } else {
                toasty.error(i18n.t('timesheet:invalidEmailAddress'));
            }
        }
    };

    onEmailChanged = (e) => {
        const val = e.currentTarget.value;
        this.setState({ currentEmailAddress: val });
    };

    onEmailForemanChanged = (e) => {
        const include = e.target.checked;
        const { review } = { ...this.state };
        review.emailForeman = include;
        this.setState({ review });
    };

    onEmailRemoved = (selected) => {
        const { review } = { ...this.state };
        const inx = review.emailAddresses.findIndex((ea) => ea === selected);
        review.emailAddresses.splice(inx, 1);
        this.setState({ review });
    };

    onMissingSigReasonChanged = (reason) => {
        const { review } = { ...this.state };
        review.missingSigReasonId = reason?.value ? reason.value : null;

        this.setState({ review });
    };

    onReuseSignatureChanged = (e) => {
        const include = e.target.checked;
        const { review } = { ...this.state };
        review.reuseClientSignature = include;
        this.setState({ review });
    };

    onSelectChanged = onReactSelectChanged;

    onResetClientSignature = async () => {
        const confirmed = await Modal.ask({
            title: i18n.t('timesheet:resetClientSignatureTitle'),
            body: i18n.t('timesheet:resetClientSignatureBody'),
            SaveButtonProps: {
                label: i18n.t('timesheet:resetClientSignatureSaveLabel'),
                savingLabel: i18n.t('timesheet:resetClientSignatureSavingLabel'),
                color: 'primary',
                icon: faRotate,
            },
        });

        if (!confirmed) return;

        const { review } = { ...this.state };
        this.setState({ saving: true });
        try {
            await httpClient.post(ApiRoutes.timesheetReview.resetClientSignature(review.timesheetId));
            toasty.success(i18n.t('timesheet:clientSignatureResetSuccess'));
            this.onClose();
        } catch (err) {
            const errorMessage = err.handledError?.message ?? err.message;
            toasty.error(
                i18n.t('timesheet:clientSignatureResetError'),
                errorMessage?.includes('timesheet:') ? i18n.t(errorMessage) : errorMessage,
            );
        } finally {
            this.setState({ saving: false });
        }
    };

    onSigSavedCallback = (sigData) => {
        const { review } = { ...this.state };
        review.reviewSignature.signatureData = sigData;
        review.newClientSignature = true;
        this.setState({ review, sigIsComplete: true });
    };

    onSignatureTypeChanged = (signatureType) => {
        let { review, sigIsComplete } = { ...this.state };

        review.signatureTypeId = signatureType.value;

        if (review.signatureTypeId === TimesheetSignatureType.ClientNoSignature) {
            if (review.reviewSignature?.signatureData) {
                review.reviewSignature.signatureData = null;
            }

            sigIsComplete = false;
        }

        this.setState({ review, sigIsComplete });
    };

    onSubmit = async () => {
        const { review } = { ...this.state };
        const { tenantSettings } = { ...this.props };

        this.setState({ saving: true });

        if (review.signatureTypeId !== TimesheetSignatureType.ClientNoSignature) {
            if (!((review.reviewSignature.signatureData
                // Make sure it's not just the mime type prefix.
                && review.reviewSignature.signatureData !== 'data:image/png;base64,') && (review.newClientSignature || review.reuseClientSignature))
            ) {
                toasty.error(i18n.t('timesheet:clientReviewSignatureRequired'));
                this.setState({ formValidated: true, saving: false });
                return;
            }
        } else {
            // no signature notes or reason is required.
            if (!tenantSettings.enablePredefinedTimesheetMissingSigReason && !review.notes) {
                toasty.error(i18n.t('timesheet:clientReviewNoSignatureReasonRequired'));
                this.setState({ formValidated: true, saving: false });
                return;
            }
            if (tenantSettings.enablePredefinedTimesheetMissingSigReason && !review.missingSigReasonId) {
                toasty.error(i18n.t('timesheet:clientReviewNoSignatureReasonSelectionRequired'));
                this.setState({ formValidated: true, saving: false });
                return;
            }
        }

        try {
            // Remove timesheet in order to reduce payload size
            await httpClient.post(ApiRoutes.timesheetReview.update(), { ...review, timeSheet: null });
            toasty.success(i18n.t('timesheet:saved'));
            this.onClose();
        } catch (err) {
            toasty.error(err.handledError.message);
        } finally {
            this.setState({ saving: false });
        }
    };

    setRef = (ref) => {
        // keep a reference to the dom ref as an instance property
        this.ref = ref;
        // give the dom ref to react-beautiful-dnd
        // this.props.innerRef(ref);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    open = async (timesheetId) => {
        this.setState(this.baseState);
        await this.populateState();

        let review = null;

        this.resetForm();

        this.setState({ loading: true });

        if (timesheetId) {
            review = await httpClient.js(ApiRoutes.timesheetReview.clientByTimesheetId(timesheetId));
        } else {
            review = null;//
        }
        const displayReuse = this.context?.user.isImpersonating && !!review.reviewSignature && !!review.reviewSignature.signatureData && !review.isComplete;
        this.props.toggleShow(true);
        this.context.setFormOpened(true);

        this.setState({ review, loading: false, displayReuse });
    };

    async populateState() {
        const [signatureTypes, missingSigReasons] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.clientSignatureTypes()),
            httpClient.js(ApiRoutes.timesheetReview.missingSignatureReasons()),
        ]);

        this.setState({
            signatureTypes,
            missingSignatureReasons: missingSigReasons,
        });
    }

    resetForm = () => {
        this.setState({
            formValidated: false,
        });
    };

    render() {
        const {
            review,
            formValidated,
            validationMessage,
            sigIsComplete,
            displayReuse,
        } = this.state;

        const {
            tenantSettings,
        } = { ...this.props };

        if (!review) return null;

        return (
            <SlideForm
                size="col-xl-6 col-lg-6 col-md-12 col-sm-12"
                loading={this.state.loading}
                show={this.props.show}
                id="timesheetReview"
                formIcon={faUserClock}
                formTitle={i18n.t('timesheet:clientReviewSignatureTitle')}
                ref={this.formRef}
                setIsValidated={(value) => {
                    this.setState({ formValidated: value });
                }}
                isValidated={formValidated}
                onClose={this.onClose}
                onDelete={this.onDelete}
                errors={this.state.errors}
                onClearErrors={this.onClearErrors}
                error={validationMessage}
                onSubmit={this.onSubmit}
                hideToolbar
            >
                <FormGroup>
                    <span className="h5 font-weight-bold text-center text-dark mb-0 pb-0">{i18n.t('timesheet:summaryOfBillableCharges')}</span>
                </FormGroup>

                <FormGroup>
                    <SignatureReviewTable timeCharges={(review ?? {}).reviewTable} />
                </FormGroup>
                {
                    !!(review ?? {}).workOrderNumber
                    && (
                        <FormGroup>
                            <FormLabel label={i18n.t('timesheet:workOrder')} />
                            <span className="ml-3 text-danger font-weight-bold">{i18n.t('timesheet:workOrderBracketed', { number: review.workOrderNumber })}</span>
                        </FormGroup>
                    )
                }

                {displayReuse && (
                    <FormCheckbox
                        labelClass="text-danger text-small"
                        className="mt-2 pl-2 mb-3"
                        id="reuseClientSignature"
                        name="reuseClientSignature"
                        readOnly={false}
                        checked={!!review.reuseClientSignature}
                        onChange={this.onReuseSignatureChanged}
                        labelText="[Admin] Re-Use Signature"
                    />
                )}

                {!review.reuseClientSignature && (
                    <ClientTimesheetSignature
                        review={review}
                        onSignatureTypeChanged={this.onSignatureTypeChanged}
                        onMissingSigReasonChanged={this.onMissingSigReasonChanged}
                        onChangeNotes={this.onChangeNotes}
                        onSigSavedCallback={this.onSigSavedCallback}
                        signatureRef={this.signatureRef}
                        tenantSettings={tenantSettings}
                        sigIsComplete={sigIsComplete}
                    />
                )}

                {!review.isComplete && (
                    <FlexCenterRow className="mt-2">
                        <FlexCenterRow className="mt-2">
                            <SaveButton
                                onClick={this.onSubmit}
                                saving={this.state.saving}
                            />
                        </FlexCenterRow>
                    </FlexCenterRow>
                )}

                {Boolean(review.isComplete && tenantSettings?.retainClientSignatureOnUpdate) && (
                    <FlexCenterRow className="mt-3">
                        <SaveButton
                            color="warning"
                            type="button"
                            label={i18n.t('timesheet:resetClientSignatureLabel')}
                            savingLabel="Resetting..."
                            icon={faRotate}
                            onClick={this.onResetClientSignature}
                            saving={this.state.saving}
                        />
                    </FlexCenterRow>
                )}
            </SlideForm>
        );
    }
}
